import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import api from '../api';

function formatDateTime(d) {
  try {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString();
  } catch {
    return '';
  }
}

function toPublicUrl(baseUrl, storedUrl) {
  const p = String(storedUrl || '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(baseUrl || '').replace(/\/$/, '');
  const rel = p.startsWith('/') ? p : `/${p}`;
  return base + rel;
}

export default function APNClipsModeration() {
  const { user } = useContext(UserContext);
  const baseUrl = useMemo(() => (import.meta.env.VITE_API_URL || 'http://localhost:5000'), []);
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: 16,
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/apn-clips/moderation/submissions', { params: { status: 'pending' } });
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      // Safety-net in case older backend returns everything.
      const pendingOnly = list.filter((s) => String(s?.moderationStatus || 'pending') === 'pending');
      setItems(pendingOnly);
    } catch (e) {
      setError(e?.response?.data?.error || 'Konnte Einreichungen nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (id, action) => {
    if (!id) return;
    setBusyId(id);
    setError('');
    try {
      if (action === 'approve') {
        await api.post(`/apn-clips/moderation/submissions/${id}/approve`);
      } else {
        await api.post(`/apn-clips/moderation/submissions/${id}/reject`, { reason: null });
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const getUserLabel = (sub) => {
    const dn = String(sub?.user?.displayName || '').trim();
    const un = String(sub?.user?.username || '').trim();
    return dn || un || `User #${sub?.userId || '?'}`;
  };

  const getModeratedByLabel = (sub) => {
    const dn = String(sub?.moderatedBy?.displayName || '').trim();
    const un = String(sub?.moderatedBy?.username || '').trim();
    return dn || un || (sub?.moderatedByUserId ? `User #${sub.moderatedByUserId}` : '');
  };

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Moderation</h2>
        <p className="hero-desc" style={{ margin: '0 auto 18px', textAlign: 'center', maxWidth: 920 }}>
          Offene Einreichungen (noch nicht freigegeben/abgelehnt) – anschauen und freigeben oder ablehnen.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '6px 0 14px' }}>
          <Link className="hero-btn-secondary" to="/apn-clips" style={{ textDecoration: 'none' }}>Zurück</Link>
          <button className="hero-btn-primary" type="button" onClick={load} disabled={loading}>Neu laden</button>
          {isAdmin && (
            <Link className="hero-btn-secondary" to="/apn-clips/moderation/admin" style={{ textDecoration: 'none' }}>
              Adminansicht
            </Link>
          )}
        </div>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Einreichungen</h2>

          {error && (
            <div style={{ padding: '0 8px 12px', color: '#ffdddd', fontSize: 13 }}>{error}</div>
          )}

          {loading ? (
            <div style={{ padding: '0 8px 8px', opacity: 0.8 }}>Lade…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '0 8px 8px', opacity: 0.8 }}>Keine offenen Einreichungen vorhanden.</div>
          ) : (
            <div style={{ display: 'grid', gap: 12, padding: '0 8px 8px' }}>
              {items.map((s) => {
                const id = s?.id;
                const moderationStatus = String(s?.moderationStatus || 'pending');
                const isReady = String(s?.status) === 'ready';
                const isFailed = String(s?.status) === 'failed';
                const isProcessing = String(s?.status) === 'processing';
                const videoUrl = isReady ? toPublicUrl(baseUrl, s?.storedUrl) : '';

                return (
                  <div key={id} className="highlight-card" style={{ ...card, alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 800, fontSize: 16 }}>
                        #{id} — {String(s?.title || '(ohne Titel)')}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>
                        Moderation: <span style={{ fontWeight: 800 }}>{moderationStatus}</span>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                      <div>
                        <div><b>Creator:</b> {String(s?.creatorName || '—')}</div>
                        <div><b>Von User:</b> {getUserLabel(s)}</div>
                        <div><b>Plattform:</b> {String(s?.platform || '—')}</div>
                        <div><b>Kategorie:</b> {String(s?.category || '—')}</div>
                        <div><b>Rechte bestätigt:</b> {s?.rightsConfirmed ? 'Ja' : 'Nein'}</div>
                      </div>
                      <div>
                        <div><b>Status:</b> {String(s?.status || '—')}</div>
                        <div><b>Dauer:</b> {Number.isFinite(Number(s?.durationSeconds)) ? `${s.durationSeconds}s` : '—'}</div>
                        <div><b>Erstellt:</b> {formatDateTime(s?.createdAt) || '—'}</div>
                        <div><b>Moderiert von:</b> {getModeratedByLabel(s) || '—'}</div>
                        <div><b>Moderiert am:</b> {formatDateTime(s?.moderatedAt) || '—'}</div>
                      </div>
                    </div>

                    {String(s?.mode) === 'link' && s?.sourceUrl && (
                      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                        <b>Quelle:</b>{' '}
                        <a href={s.sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                          {String(s.sourceUrl)}
                        </a>
                      </div>
                    )}

                    {isFailed && (
                      <div style={{ marginTop: 10, color: '#ffdddd', fontSize: 13 }}>
                        {String(s?.errorMessage || 'Verarbeitung fehlgeschlagen.')}
                      </div>
                    )}

                    {isProcessing && (
                      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
                        Verarbeitung läuft…
                      </div>
                    )}

                    {isReady && videoUrl && (
                      <div style={{ marginTop: 12 }}>
                        <video
                          controls
                          src={videoUrl}
                          style={{ width: '100%', maxWidth: 920, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)' }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <button
                        type="button"
                        className="hero-btn-primary"
                        disabled={!isReady || busyId === id || moderationStatus === 'approved'}
                        onClick={() => decide(id, 'approve')}
                        title={!isReady ? 'Beitrag ist noch nicht bereit' : ''}
                      >
                        Freigeben
                      </button>
                      <button
                        type="button"
                        className="hero-btn-secondary"
                        disabled={busyId === id || moderationStatus === 'rejected'}
                        onClick={() => decide(id, 'reject')}
                      >
                        Ablehnen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
